import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
} from "react-bootstrap";

export default class HotelGuestManager extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      guests: [],
      selectedGuest: null, // full guest object when viewing or editing

      // UI state
      showGuestModal: false,
      guestForm: this._emptyGuestForm(),
      isEditing: false,

      showAddressesModal: false,
      addresses: [],
      addressForm: this._emptyAddressForm(),

      showPrefsModal: false,
      preferences: [],
      prefForm: this._emptyPrefForm(),
    };
  }

  // -------------------- Helpers --------------------
  _emptyGuestForm() {
    return {
      guestId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dob: "",
    };
  }

  _emptyAddressForm() {
    return {
      guestAddressId: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    };
  }

  _emptyPrefForm() {
    return {
      guestPreferenceId: "",
      preferenceKey: "",
      preferenceValue: "",
    };
  }

  _getStoreId() {
    return this.context.storeId;
  }

  _apiBase() {
    return process.env.REACT_APP_API;
  }

  _apiUrl(path) {
    const storeId = this._getStoreId();
    const base = this._apiBase();
    const sep = path.includes("?") ? "&" : "?";
    return `${base}${path}${sep}storeId=${encodeURIComponent(storeId)}`;
  }

  // -------------------- Lifecycle --------------------
  componentDidMount() {
    this.loadGuests();
  }

  // -------------------- Guests --------------------
  async loadGuests() {
    const storeId = this._getStoreId();
    if (!storeId) {
      this.setState({ error: "storeId query param or prop required" });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(this._apiUrl("HotelGuest"));
      if (!res.ok) throw new Error(await res.text());
      const guests = await res.json();
      this.setState({ guests });
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  }

  openCreateGuest = () => {
    this.setState({
      showGuestModal: true,
      isEditing: false,
      guestForm: this._emptyGuestForm(),
    });
  };

  openEditGuest = (guest) => {
    this.setState({
      showGuestModal: true,
      isEditing: true,
      guestForm: { ...guest },
    });
  };

  closeGuestModal = () =>
    this.setState({ showGuestModal: false, guestForm: this._emptyGuestForm() });

  handleGuestFormChange = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ guestForm: { ...s.guestForm, [name]: value } }));
  };

  submitGuest = async (e) => {
    e.preventDefault();
    const { guestForm, isEditing } = this.state;
    const storeId = this._getStoreId();

    if (!storeId) {
      this.setState({ error: "Store ID is missing in context." });
      return;
    }

    try {
      this.setState({ loading: true, error: null });

      // Prepare payload by copying and sanitizing fields that would break model binding
      const payload = { ...guestForm };

      // Remove guestId for create (server will generate one)
      if (!isEditing) {
        delete payload.guestId;
        // ensure storeId is provided in body too (optional; controller reads query or claim)
        payload.storeId = storeId;
      }

      // Normalize DOB: if empty string, send null (server expects nullable DateTime)
      if (
        payload.dob === "" ||
        payload.dob === null ||
        payload.dob === undefined
      ) {
        delete payload.dob; // or payload.dob = null;
      } else {
        // ensure ISO date string (server binds ISO-8601)
        // If your guestForm.dob is from <input type="date"> it's already yyyy-mm-dd
        // Convert to full ISO string if needed:
        // payload.dob = new Date(payload.dob).toISOString();
      }

      let url = `${this._apiBase()}HotelGuest`;
      let method = "POST";

      if (isEditing) {
        if (!guestForm.guestId)
          throw new Error("Guest ID is missing for update.");
        url = `${this._apiBase()}HotelGuest/${guestForm.guestId}`;
        method = "PUT";
      }

      // Append storeId query param (your _apiUrl helper also does this — you can use it instead)
      const sep = url.includes("?") ? "&" : "?";
      url = `${url}${sep}storeId=${encodeURIComponent(storeId)}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // read text (server often returns a useful message)
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      // On success reload
      await this.loadGuests();
      this.closeGuestModal();
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  deleteGuest = async (guest) => {
    if (!window.confirm(`Delete guest ${guest.firstName} ${guest.lastName}?`))
      return;
    try {
      this.setState({ loading: true, error: null });
      const res = await fetch(this._apiUrl(`HotelGuest/${guest.guestId}`), {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(await res.text());
      await this.loadGuests();
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  // -------------------- Addresses --------------------
  openAddresses = async (guest) => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(
        this._apiUrl(`HotelGuest/${guest.guestId}/addresses`)
      );
      if (!res.ok) throw new Error(await res.text());
      const addresses = await res.json();
      this.setState({
        addresses,
        selectedGuest: guest,
        showAddressesModal: true,
      });
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  closeAddresses = () =>
    this.setState({
      showAddressesModal: false,
      addresses: [],
      addressForm: this._emptyAddressForm(),
      selectedGuest: null,
    });

  handleAddressChange = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({
      addressForm: { ...s.addressForm, [name]: value },
    }));
  };

  addAddress = async (e) => {
    e.preventDefault();
    const { selectedGuest, addressForm } = this.state;
    try {
      this.setState({ loading: true, error: null });
      const res = await fetch(
        this._apiUrl(`HotelGuest/${selectedGuest.guestId}/addresses`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(addressForm),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const added = await res.json();
      this.setState((s) => ({
        addresses: [...s.addresses, added],
        addressForm: this._emptyAddressForm(),
      }));
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  deleteAddress = async (address) => {
    const { selectedGuest } = this.state;
    if (!window.confirm("Delete address?")) return;
    try {
      this.setState({ loading: true, error: null });
      const res = await fetch(
        this._apiUrl(
          `HotelGuest/${selectedGuest.guestId}/addresses/${address.guestAddressId}`
        ),
        {
          method: "DELETE",
        }
      );
      if (!res.ok) throw new Error(await res.text());
      this.setState((s) => ({
        addresses: s.addresses.filter(
          (a) => a.guestAddressId !== address.guestAddressId
        ),
      }));
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  // -------------------- Preferences --------------------
  openPreferences = async (guest) => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(
        this._apiUrl(`HotelGuest/${guest.guestId}/preferences`)
      );
      if (!res.ok) throw new Error(await res.text());
      const preferences = await res.json();
      this.setState({
        preferences,
        selectedGuest: guest,
        showPrefsModal: true,
      });
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  closePreferences = () =>
    this.setState({
      showPrefsModal: false,
      preferences: [],
      prefForm: this._emptyPrefForm(),
      selectedGuest: null,
    });

  handlePrefChange = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ prefForm: { ...s.prefForm, [name]: value } }));
  };

  addPreference = async (e) => {
    e.preventDefault();
    const { selectedGuest, prefForm } = this.state;
    try {
      this.setState({ loading: true, error: null });
      const res = await fetch(
        this._apiUrl(`HotelGuest/${selectedGuest.guestId}/preferences`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(prefForm),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const added = await res.json();
      this.setState((s) => ({
        preferences: [...s.preferences, added],
        prefForm: this._emptyPrefForm(),
      }));
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  deletePreference = async (pref) => {
    const { selectedGuest } = this.state;
    if (!window.confirm("Delete preference?")) return;
    try {
      this.setState({ loading: true, error: null });
      const res = await fetch(
        this._apiUrl(
          `HotelGuest/${selectedGuest.guestId}/preferences/${pref.guestPreferenceId}`
        ),
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(await res.text());
      this.setState((s) => ({
        preferences: s.preferences.filter(
          (p) => p.guestPreferenceId !== pref.guestPreferenceId
        ),
      }));
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  // -------------------- Render --------------------
  render() {
    const {
      loading,
      error,
      guests,
      showGuestModal,
      guestForm,
      isEditing,
      showAddressesModal,
      addresses,
      addressForm,
      showPrefsModal,
      preferences,
      prefForm,
      selectedGuest,
    } = this.state;

    return (
      <div className="container">
        <Row className=" mb-3 align-items-center">
          <Col>
            <h4>Hotel Guests</h4>
          </Col>
          <Col className="text-end">
            <Button onClick={this.openCreateGuest}>Add Guest</Button>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {loading && <Spinner animation="border" />}

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>DOB</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {guests.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center">
                  No guests found
                </td>
              </tr>
            )}
            {guests.map((g) => (
              <tr key={g.guestId}>
                <td>{g.firstName}</td>
                <td>{g.lastName}</td>
                <td>{g.email}</td>
                <td>{g.phone}</td>
                <td>{g.dob ? new Date(g.dob).toLocaleDateString() : ""}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="me-1"
                    onClick={() => this.openEditGuest(g)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="me-1"
                    onClick={() => this.openAddresses(g)}
                  >
                    Addresses
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-info"
                    className="me-1"
                    onClick={() => this.openPreferences(g)}
                  >
                    Preferences
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => this.deleteGuest(g)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Guest Modal */}
        <Modal show={showGuestModal} onHide={this.closeGuestModal}>
          <Form onSubmit={this.submitGuest}>
            <Modal.Header closeButton>
              <Modal.Title>
                {isEditing ? "Edit Guest" : "Create Guest"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-2">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  name="firstName"
                  value={guestForm.firstName}
                  onChange={this.handleGuestFormChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  name="lastName"
                  value={guestForm.lastName}
                  onChange={this.handleGuestFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={guestForm.email}
                  onChange={this.handleGuestFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  name="phone"
                  value={guestForm.phone}
                  onChange={this.handleGuestFormChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>DOB</Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={guestForm.dob ? guestForm.dob.split("T")[0] : ""}
                  onChange={this.handleGuestFormChange}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={this.closeGuestModal}>
                Cancel
              </Button>
              <Button type="submit">{isEditing ? "Save" : "Create"}</Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Addresses Modal */}
        <Modal show={showAddressesModal} onHide={this.closeAddresses} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Addresses -{" "}
              {selectedGuest
                ? `${selectedGuest.firstName} ${selectedGuest.lastName}`
                : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Table responsive>
              <thead>
                <tr>
                  <th>Address</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Country</th>
                  <th>Postal</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {addresses.map((a) => (
                  <tr key={a.guestAddressId}>
                    <td>
                      {a.addressLine1} {a.addressLine2}
                    </td>
                    <td>{a.city}</td>
                    <td>{a.state}</td>
                    <td>{a.country}</td>
                    <td>{a.postalCode}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => this.deleteAddress(a)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {addresses.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center">
                      No addresses
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <hr />
            <h6>Add Address</h6>
            <Form onSubmit={this.addAddress}>
              <Row>
                <Col md={6} className="mb-2">
                  <Form.Control
                    name="addressLine1"
                    value={addressForm.addressLine1}
                    onChange={this.handleAddressChange}
                    placeholder="Address line 1"
                    required
                  />
                </Col>
                <Col md={6} className="mb-2">
                  <Form.Control
                    name="addressLine2"
                    value={addressForm.addressLine2}
                    onChange={this.handleAddressChange}
                    placeholder="Address line 2"
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Control
                    name="city"
                    value={addressForm.city}
                    onChange={this.handleAddressChange}
                    placeholder="City"
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Control
                    name="state"
                    value={addressForm.state}
                    onChange={this.handleAddressChange}
                    placeholder="State"
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Control
                    name="postalCode"
                    value={addressForm.postalCode}
                    onChange={this.handleAddressChange}
                    placeholder="Postal Code"
                  />
                </Col>
                <Col md={6} className="mb-2">
                  <Form.Control
                    name="country"
                    value={addressForm.country}
                    onChange={this.handleAddressChange}
                    placeholder="Country"
                  />
                </Col>
                <Col md={6} className="text-end">
                  <Button type="submit">Add Address</Button>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeAddresses}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Preferences Modal */}
        <Modal show={showPrefsModal} onHide={this.closePreferences} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Preferences -{" "}
              {selectedGuest
                ? `${selectedGuest.firstName} ${selectedGuest.lastName}`
                : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Table responsive>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Value</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {preferences.map((p) => (
                  <tr key={p.guestPreferenceId}>
                    <td>{p.preferenceKey}</td>
                    <td>{p.preferenceValue}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => this.deletePreference(p)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {preferences.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center">
                      No preferences
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <hr />
            <h6>Add Preference</h6>
            <Form onSubmit={this.addPreference}>
              <Row>
                <Col md={5} className="mb-2">
                  <Form.Control
                    name="preferenceKey"
                    value={prefForm.preferenceKey}
                    onChange={this.handlePrefChange}
                    placeholder="Key"
                    required
                  />
                </Col>
                <Col md={5} className="mb-2">
                  <Form.Control
                    name="preferenceValue"
                    value={prefForm.preferenceValue}
                    onChange={this.handlePrefChange}
                    placeholder="Value"
                  />
                </Col>
                <Col md={2} className="text-end">
                  <Button type="submit">Add</Button>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closePreferences}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
